import { useCallback, useEffect, useMemo, useState } from "react";
import {
  dealerExcludedCsvUrl,
  dealerReviewQueueCsvUrl,
  exportDealerNetwork100,
  fetchDealerCoverageDashboard,
} from "../../../../services/dealerCoverageApi";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import AdminTableContainer from "../layout/AdminTableContainer";

function SummaryCard({ label, value, hint }) {
  return (
    <div className="food-comparison-summary-cards__card">
      <span className="muted">{label}</span>
      <strong>{value ?? "—"}</strong>
      {hint ? <span className="muted admin-source-controls-dash__type-note">{hint}</span> : null}
    </div>
  );
}

const DealerCoverageDashboardPage = ({ onClose, onOpenDealers, onOpenReview, onOpenDealerDetail }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [exportMessage, setExportMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchDealerCoverageDashboard({ reviewLimit: 15, excludedLimit: 15 });
      setDashboard(data);
    } catch (err) {
      setErrorMessage(err.message || "Unable to load dealer coverage dashboard.");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = dashboard?.summary;
  const progressPct = useMemo(() => {
    if (!summary?.goal) return 0;
    return Math.min(100, Math.round((summary.eligibleDealerSources / summary.goal) * 100));
  }, [summary]);

  const handleExport100 = async () => {
    if (!canManage) return;
    try {
      setExportMessage("Exporting…");
      const result = await exportDealerNetwork100();
      setExportMessage(
        `Exported ${result.exportedCount} dealers to ${result.csvPath || "exports/automotive/dealer-network-100.csv"}`
      );
    } catch (err) {
      setExportMessage(err.message || "Export failed");
    }
  };

  const openDetail = (profileKey) => {
    if (onOpenDealerDetail) {
      onOpenDealerDetail(profileKey);
      return;
    }
    window.location.hash = `#/admin/automotive-dealer-detail/${encodeURIComponent(profileKey)}`;
  };

  return (
    <div className="admin-page automotive-dealer-coverage-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Automotive · Dealer Registry</p>
          <h1>Dealer Coverage Dashboard</h1>
          <p className="muted">
            100-source network health for Medellín / Antioquia. Uses M16–M18 registry reports as source of truth.
          </p>
        </div>
        <div className="admin-page__actions">
          <button type="button" className="admin-btn admin-btn--secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {errorMessage ? <p className="admin-error">{errorMessage}</p> : null}
      {exportMessage ? <p className="muted">{exportMessage}</p> : null}

      {loading && !dashboard ? <p className="muted">Loading dashboard…</p> : null}

      {summary ? (
        <>
          <section className="food-comparison-summary-cards food-access-coverage-dashboard__cards">
            <SummaryCard label="Eligible sources" value={summary.eligibleDealerSources} hint={`Goal ${summary.goal}`} />
            <SummaryCard label="Remaining to goal" value={summary.remainingToGoal} />
            <SummaryCard label="Verified profiles" value={summary.verifiedProfiles} />
            <SummaryCard label="Needs review" value={summary.needsReviewProfiles} />
            <SummaryCard label="Duplicate candidates" value={summary.duplicateCandidates} />
            <SummaryCard label="Direct inventory" value={summary.directInventoryDealers} />
            <SummaryCard label="Marketplace-only" value={summary.marketplaceOnlyDealers} />
            <SummaryCard label="Brand locator" value={summary.brandLocatorDealers} />
            <SummaryCard label="Social / directory / manual" value={summary.socialDirectoryManualDealers} />
            <SummaryCard label="Branch profiles" value={summary.branchProfiles} />
            <SummaryCard label="New vehicle dealers" value={summary.newVehicleDealers} />
            <SummaryCard label="Used vehicle dealers" value={summary.usedVehicleDealers} />
            <SummaryCard label="Both new & used" value={summary.dealersSellingBoth} />
            <SummaryCard label="Official brand dealers" value={summary.officialBrandDealers} />
            <SummaryCard label="Excluded profiles" value={summary.excludedProfiles} />
            <SummaryCard label="Stale profiles" value={summary.staleProfiles} />
          </section>

          <section className="panel-card" style={{ marginTop: "1rem" }}>
            <h3>Progress to {summary.goal}</h3>
            <div className="admin-progress-bar" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
              <div className="admin-progress-bar__fill" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="muted">
              {summary.goalReached ? "Goal reached." : `${summary.remainingToGoal} sources remaining.`}
            </p>
          </section>

          <div className="admin-page__toolbar" style={{ marginTop: "1rem", gap: "0.5rem", display: "flex", flexWrap: "wrap" }}>
            {onOpenDealers ? (
              <button type="button" className="admin-btn admin-btn--secondary" onClick={onOpenDealers}>
                Dealer list
              </button>
            ) : (
              <a className="admin-btn admin-btn--secondary" href="#/admin/automotive-dealers">
                Dealer list
              </a>
            )}
            {onOpenReview ? (
              <button type="button" className="admin-btn admin-btn--secondary" onClick={onOpenReview}>
                Review queue
              </button>
            ) : (
              <a className="admin-btn admin-btn--secondary" href="#/admin/automotive-dealer-review">
                Review queue
              </a>
            )}
            <a className="admin-btn admin-btn--ghost" href={dealerReviewQueueCsvUrl()} target="_blank" rel="noreferrer">
              Export review queue CSV
            </a>
            <a className="admin-btn admin-btn--ghost" href={dealerExcludedCsvUrl()} target="_blank" rel="noreferrer">
              Export excluded CSV
            </a>
            <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealer-governance">
              Governance
            </a>
            {canManage ? (
              <button type="button" className="admin-btn admin-btn--ghost" onClick={handleExport100}>
                Export 100-source registry
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      {dashboard?.sourceTypes?.rows?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>Source type distribution</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Source type</th>
                <th>Eligible</th>
                <th>Total profiles</th>
                <th>Needs review</th>
                <th>Stale</th>
                <th>% of goal</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.sourceTypes.rows.map((row) => (
                <tr key={row.sourceType}>
                  <td>{row.sourceType}</td>
                  <td>{row.eligibleCount}</td>
                  <td>{row.totalProfiles}</td>
                  <td>{row.needsReviewCount}</td>
                  <td>{row.staleCount}</td>
                  <td>{row.percentageOfGoal}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {dashboard?.cities?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>City coverage</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>City</th>
                <th>Eligible</th>
                <th>Direct</th>
                <th>Marketplace</th>
                <th>Brand locator</th>
                <th>New</th>
                <th>Used</th>
                <th>Both</th>
                <th>Needs review</th>
                <th>Excluded</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.cities.map((row) => (
                <tr key={row.city} className={row.city === "Unknown" ? "admin-table__row--warn" : ""}>
                  <td>{row.city}</td>
                  <td>{row.eligibleDealerCount}</td>
                  <td>{row.directInventoryCount}</td>
                  <td>{row.marketplaceCount}</td>
                  <td>{row.brandLocatorCount}</td>
                  <td>{row.newVehicleCount}</td>
                  <td>{row.usedVehicleCount}</td>
                  <td>{row.bothNewUsedCount}</td>
                  <td>{row.needsReviewCount}</td>
                  <td>{row.excludedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {dashboard?.brands?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>Brand coverage (top 20)</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Eligible</th>
                <th>Official</th>
                <th>New</th>
                <th>Used</th>
                <th>CPO</th>
                <th>Direct</th>
                <th>Marketplace</th>
                <th>Brand locator</th>
                <th>Cities</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.brands.slice(0, 20).map((row) => (
                <tr key={row.brandName}>
                  <td>{row.brandName}</td>
                  <td>{row.eligibleDealerCount}</td>
                  <td>{row.officialDealerCount}</td>
                  <td>{row.newVehicleDealerCount}</td>
                  <td>{row.usedVehicleDealerCount}</td>
                  <td>{row.certifiedPreownedCount}</td>
                  <td>{row.directInventoryCount}</td>
                  <td>{row.marketplaceCount}</td>
                  <td>{row.brandLocatorCount}</td>
                  <td>{row.cityCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {dashboard?.reviewQueue?.items?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>Review queue snapshot</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Dealer</th>
                <th>Action</th>
                <th>Confidence</th>
                <th>Reasons</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.reviewQueue.items.map((item) => (
                <tr key={item.profileKey}>
                  <td>
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() => openDetail(item.profileKey)}
                    >
                      {item.displayName}
                    </button>
                  </td>
                  <td>{item.recommendedAction}</td>
                  <td>{item.confidence}</td>
                  <td>{(item.reasons || []).slice(0, 2).join("; ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {dashboard?.excluded?.items?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>Excluded profiles snapshot</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Dealer</th>
                <th>Reason</th>
                <th>Recommended action</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.excluded.items.slice(0, 15).map((item) => (
                <tr key={item.profileKey || item.dealerProfileId}>
                  <td>
                    {item.profileKey ? (
                      <button
                        type="button"
                        className="admin-link-btn"
                        onClick={() => openDetail(item.profileKey)}
                      >
                        {item.displayName}
                      </button>
                    ) : (
                      item.displayName
                    )}
                  </td>
                  <td>{item.reasonExcluded}</td>
                  <td>{item.recommendedAction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {dashboard?.topDirectCandidates?.length ? (
        <AdminTableContainer className="admin-table-section">
          <h3>Top direct ingestion candidates</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Dealer</th>
                <th>City</th>
                <th>Score</th>
                <th>Tier</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.topDirectCandidates.slice(0, 10).map((row, idx) => (
                <tr key={row.profileKey || row.dealerProfileId || idx}>
                  <td>{row.rank ?? idx + 1}</td>
                  <td>
                    {row.profileKey ? (
                      <button
                        type="button"
                        className="admin-link-btn"
                        onClick={() => openDetail(row.profileKey)}
                      >
                        {row.displayName}
                      </button>
                    ) : (
                      row.displayName
                    )}
                  </td>
                  <td>{row.city || row.primaryCity}</td>
                  <td>{row.overallScore ?? "—"}</td>
                  <td>{row.recommendedTier ?? "—"}</td>
                  <td>{row.recommendedAction ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}
    </div>
  );
};

export default DealerCoverageDashboardPage;
